/**
 * NavigationMenu Component
 * Role-based hamburger navigation menu with grouped submenus
 */

import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dropdown, Badge } from 'antd';
import usePendingUsersCount from '../../hooks/usePendingUsersCount';
import { 
  MenuOutlined,
  InfoCircleOutlined,
  MailOutlined,
  PlayCircleOutlined,
  UserOutlined,
  FormOutlined,
  SettingOutlined,
  GlobalOutlined,
  FileTextOutlined,
  CloudServerOutlined,
  DeleteOutlined,
  DatabaseOutlined,
  BookOutlined,
  CalendarOutlined,
  ScheduleOutlined,
  QuestionCircleOutlined,
  ExperimentOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { AppRoles } from '../../services/authService';
import { routes } from '../../routes';
import { MenuGroups } from '../../constants/menuGroups';
import styles from './NavigationMenu.module.css';

/**
 * Icon mapping for menu items by menuKey
 */
const menuIcons = {
  // Public
  about: <InfoCircleOutlined />,
  contact: <MailOutlined />,
  simulations: <PlayCircleOutlined />,
  terms: <BookOutlined />,
  events: <CalendarOutlined />,

  // Account
  profile: <UserOutlined />,
  // Content
  surveys: <FormOutlined />,
  quizCollections: <QuestionCircleOutlined />,
  learningPaths: <ApartmentOutlined />,
  manageEvents: <ScheduleOutlined />,
  trainingProfiles: <ExperimentOutlined />,
  // Admin
  users: <UserOutlined />,
  countries: <GlobalOutlined />,
  levelsOfDifficulty: <SettingOutlined />,
  exceptionLogs: <FileTextOutlined />,
  fileStorage: <CloudServerOutlined />,
  deletedFiles: <DeleteOutlined />,
  dataDeletionLogs: <DatabaseOutlined />,
};

/**
 * Menu group configuration
 */
const menuGroupConfig = {
  [MenuGroups.PUBLIC]: {
    labelKey: 'nav.general',
    order: 1,
  },
  [MenuGroups.ACCOUNT]: {
    labelKey: 'nav.account',
    order: 2,
    requiresAuth: true,
  },
  [MenuGroups.CONTENT]: {
    labelKey: 'nav.content',
    order: 3,
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  [MenuGroups.TRAINING]: {
    labelKey: 'nav.training',
    order: 4,
    roles: [AppRoles.Admin, AppRoles.ContentCreator, AppRoles.MasterTrainer, AppRoles.Trainer, AppRoles.Trainee],
  },
  [MenuGroups.ADMIN]: {
    labelKey: 'nav.admin',
    order: 5,
    roles: [AppRoles.Admin],
  },
};

/**
 * NavigationMenu Component
 * Displays a hamburger menu with role-based items organized in groups
 */
const NavigationMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, hasAnyRole } = useAuth();
  const { pendingCount } = usePendingUsersCount();

  /**
   * Build menu items based on routes and user permissions
   */
  const menuItems = useMemo(() => {
    /**
     * Check if user can access a menu group
     */
    const canAccessGroup = (groupKey) => {
      const config = menuGroupConfig[groupKey];
      if (!config) return false;
      
      // Check auth requirement
      if (config.requiresAuth && !isAuthenticated) return false;
      
      // Check role requirement
      if (config.roles && config.roles.length > 0) {
        return hasAnyRole(config.roles);
      }
      
      return true;
    };

    /**
     * Check if user can access a route
     */
    const canAccessRoute = (route) => {
      // Check auth requirement
      if (route.requiresAuth && !isAuthenticated) return false;
      
      // Check role requirement
      if (route.roles && route.roles.length > 0) {
        return hasAnyRole(route.roles);
      }
      
      return true;
    };

    const items = [];
    
    // Group routes by menuGroup
    const routesByGroup = {};
    routes.forEach(route => {
      if (route.menuGroup && canAccessRoute(route)) {
        if (!routesByGroup[route.menuGroup]) {
          routesByGroup[route.menuGroup] = [];
        }
        routesByGroup[route.menuGroup].push(route);
      }
    });

    // Sort groups by order and build menu items
    const sortedGroups = Object.keys(routesByGroup)
      .filter(groupKey => canAccessGroup(groupKey))
      .sort((a, b) => (menuGroupConfig[a]?.order || 99) - (menuGroupConfig[b]?.order || 99));

    sortedGroups.forEach((groupKey, groupIndex) => {
      const config = menuGroupConfig[groupKey];
      const groupRoutes = routesByGroup[groupKey];

      // Add divider before group (except first)
      if (groupIndex > 0) {
        items.push({
          key: `divider-${groupKey}`,
          type: 'divider',
        });
      }

      // Add group header
      items.push({
        key: `group-${groupKey}`,
        type: 'group',
        label: t(config.labelKey),
        children: groupRoutes.map(route => {
          const isActive = location.pathname === route.path;
          const baseLabel = t(`nav.${route.menuKey}`);
          
          // Add badge for users menu item if there are pending users
          const label = route.menuKey === 'users' && pendingCount > 0 ? (
            <Badge count={pendingCount} size="small" offset={[8, 0]}>
              {baseLabel}
            </Badge>
          ) : baseLabel;

          return {
            key: route.path,
            icon: menuIcons[route.menuKey] || null,
            label,
            className: isActive ? styles.activeItem : '',
            onClick: () => navigate(route.path),
          };
        }),
      });
    });

    return items;
  }, [t, navigate, location.pathname, isAuthenticated, hasAnyRole, pendingCount]);

  // Don't render if no menu items
  if (menuItems.length === 0) {
    return null;
  }

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
      placement="bottomLeft"
      overlayClassName={styles.dropdownOverlay}
    >
      <button 
        className={styles.menuButton}
        aria-label={t('nav.menu')}
        title={t('nav.menu')}
      >
        <MenuOutlined className={styles.menuIcon} />
      </button>
    </Dropdown>
  );
};

export default NavigationMenu;
